﻿import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ExternalLink, FileText, Inbox, Link2, RefreshCcw, Sparkles, UserRoundPlus } from 'lucide-react';
import * as repo from './IngestionHubRepository';

import { auth } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import { SmartEntityLinker } from '../../components/common/SmartEntityLinker';
import { extractDocumentData } from '../../services/AIDocumentService';
import { resolveDocumentUrl } from '../../utils/documentResolver';
import { generateDocumentFileStem } from '../../utils/documentUtils';
import { toTitleCasePt } from '../../utils/formatters';
import { WhatsAppImporter } from '../tools/WhatsAppImporter';

const appId = FIREBASE_CONFIG.projectId;
const DRIVE_TREE_VERSION = '2026.04-caderno-v3';
const DRIVE_TREE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const MODE_META = {
  triage: {
    title: 'Sala de Ingestão',
    subtitle: 'Gerencie uploads do Portal do Cliente e arquivos não vinculados.',
    destinationLabel: 'Destino jurídico da triagem',
    entityPlaceholder: 'Buscar cliente ou processo para o WhatsApp...',
    listTitle: 'Sala de Ingestão',
  },
  whatsapp: {
    title: 'Perícia WhatsApp',
    subtitle: 'Selecione um arquivo ZIP (ou pasta) para iniciar o parser via Edge Computing (Porta 8009).',
    destinationLabel: 'Destino jurídico da perícia',
    entityPlaceholder: 'Buscar cliente ou processo para a perícia de WhatsApp...',
    listTitle: 'Perícia WhatsApp',
  },
};

const isBlank = (v) => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return !s;
};

const toDateLabel = (v) => {
  try {
    if (v?.toDate) return v.toDate().toLocaleString();
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
};

const toMillis = (v) => {
  if (v?.toDate) return v.toDate().getTime();
  const d = new Date(v || 0);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const resolveClientName = (row) => {
  return String(row?.clientName || row?.client?.name || row?.client?.fullName || '').trim() || 'Cliente';
};

const resolveFileName = (row) => {
  return String(row?.fileName || row?.filename || row?.title || row?.name || '').trim() || 'Arquivo';
};

const resolveExtension = (fileName) => {
  const safe = String(fileName || '').trim();
  const idx = safe.lastIndexOf('.');
  if (idx <= 0 || idx === safe.length - 1) return '';
  return safe.slice(idx).replace(/[^.a-zA-Z0-9_-]/g, '');
};

const resolveFileUrl = (row) => {
  return String(row?.fileUrl || row?.url || row?.downloadUrl || row?.webViewLink || row?.webContentLink || '').trim();
};

const readText = (value) => String(value || '').trim();

const getBackendBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

const shouldRunDriveAutoHealFromClientSnapshot = (client) => {
  const driveFolderId = readText(client?.driveFolderId || client?.drive?.rootFolderId || client?.drive?.folderId);
  if (!driveFolderId) return false;

  const driveFolders = client?.driveFolders;
  const healthy = client?.driveTreeHealthy !== false;
  const version = readText(client?.driveTreeVersion);
  const lastCheckedAt = toMillis(client?.driveTreeLastCheckedAt);
  const isFresh = lastCheckedAt > 0 && (Date.now() - lastCheckedAt) < DRIVE_TREE_MAX_AGE_MS;
  const hasFolders = driveFolders && typeof driveFolders === 'object' && readText(driveFolders?.processos);

  if (!healthy) return true;
  if (!hasFolders) return true;
  if (version !== DRIVE_TREE_VERSION) return true;
  if (!isFresh) return true;
  return false;
};

const resolveMatterLabel = (matter) => {
  return readText(matter?.title || matter?.name || matter?.tipoAcao || matter?.assunto || matter?.numeroCNJ || 'Processo');
};

const buildArchiveFileName = ({ row, context, sourceKind }) => {
  const originalName = resolveFileName(row);
  const extension = resolveExtension(originalName);
  const mainPartyName = readText(context?.clientName) || resolveClientName(row) || 'Cliente';
  const rawDocType = sourceKind === 'portal_upload'
    ? 'Triagem'
    : readText(row?.category || row?.classification?.category || row?.documentType || 'Documento');
  const stem = generateDocumentFileStem(rawDocType, { name: mainPartyName }, {
    mainPartyName,
    originalFileName: originalName,
    fileName: originalName,
    processName: readText(context?.matterName),
    shortName: readText(context?.matterName),
  });
  return `${stem || 'Documento-Cliente'}${extension}`;
};

const writeClientNote = async ({ clientId, type, actorName, payload }) => {
  await repo.writeClientNote({ appId, clientId, type, actorName, payload });
};

const deleteDriveFileSilently = async (fileId) => {
  const safeFileId = readText(fileId);
  if (!safeFileId) return;
  try {
    await fetch(`${getBackendBaseUrl()}/drive/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileId: safeFileId }),
    });
  } catch {
    void 0;
  }
};

const openPathOnSystem = async (path) => {
  const safePath = readText(path);
  if (!safePath) return;
  try {
    await fetch(`${getBackendBaseUrl()}/system/open-path`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: safePath }),
    });
  } catch {
    void 0;
  }
};

const moveDriveFile = async ({ fileId, targetFolderId, desiredName }) => {
  const safeFileId = readText(fileId);
  const safeTargetFolderId = readText(targetFolderId);
  if (!safeFileId || !safeTargetFolderId) {
    throw new Error('fileId/targetFolderId inválidos para movimentação compensatória.');
  }

  const response = await fetch(`${getBackendBaseUrl()}/drive/move-file`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileId: safeFileId,
      targetFolderId: safeTargetFolderId,
      desiredName: readText(desiredName) || undefined,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(payload?.detail || payload?.error || 'Falha ao mover arquivo no Google Drive.'));
  }
  return payload;
};

const buildWhatsAppJsonFile = ({ payload, sourcePath, linkedEntity }) => {
  const stamp = new Date();
  const safeEntityName = readText(linkedEntity?.name || linkedEntity?.id || 'Vinculado').replace(/[^a-zA-Z0-9-_ ]/g, ' ').replace(/\s+/g, ' ').trim() || 'Vinculado';
  const fileName = `${stamp.toISOString().slice(0, 19).replace(/[:T]/g, '-')}__WhatsApp__${safeEntityName}.json`;
  const body = JSON.stringify({
    exportedAt: stamp.toISOString(),
    sourcePath: readText(sourcePath),
    linkedEntity: linkedEntity || null,
    payload: payload || {},
  }, null, 2);
  return new File([body], fileName, { type: 'application/json' });
};

const pushClientPrefillToBrowserState = (payload) => {
  if (typeof window === 'undefined') return false;
  try {
    const nextState = {
      ...(window.history?.state && typeof window.history.state === 'object' ? window.history.state : {}),
      prefillData: payload?.prefillData || null,
      sourceDocumentId: payload?.sourceDocumentId || '',
    };
    window.history.replaceState(nextState, '', '/clientes/novo');
    return true;
  } catch {
    return false;
  }
};

const uploadToBackendDrive = async (file, fileName, parentId = null) => {
  // Obter token Firebase para autenticação
  const token = await getToken();
  if (!token) {
    throw new Error('Usuário não autenticado. Faça login novamente.');
  }

  const resolvedParentId = String(parentId || '').trim();

  const formData = new FormData();
  formData.append('file', file, fileName);
  formData.append('fileName', fileName);
  if (resolvedParentId) formData.append('parentId', resolvedParentId);
  formData.append('makePublic', 'true');

  const res = await fetch(`${getBackendBaseUrl()}/drive/upload-file`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ detail: 'Erro desconhecido' }));
    throw new Error(errorData.detail || 'Falha no upload via Service Account');
  }

  const result = await res.json();
  return {
    storageEngineData: {
      fileId: result.id || result.fileId,
      id: result.id || result.fileId,
      webViewLink: result.webViewLink,
      webContentLink: result.webContentLink,
    },
  };
};

export const IngestionHub = ({ mode = 'triage', reloadTrigger = 0, onCreateClientFromAI } = {}) => {
  const [orphanDocs, setOrphanDocs] = useState([]);
  const [portalUploads, setPortalUploads] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');

  const [selectedDocId, setSelectedDocId] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [whatsAppTargetEntity, setWhatsAppTargetEntity] = useState(null);

  const [linking, setLinking] = useState(false);
  const [workingId, setWorkingId] = useState('');
  const [workingAction, setWorkingAction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState('');
  const [resolvedFileUrl, setResolvedFileUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [whatsAppDraft, setWhatsAppDraft] = useState(null);

  const modeMeta = MODE_META[mode] || MODE_META.triage;
  const pageTitle = modeMeta.title;
  const pageSubtitle = modeMeta.subtitle;
  const listTitle = modeMeta.listTitle;
  const isWhatsAppMode = mode === 'whatsapp';

  const docs = useMemo(() => {
    return [...portalUploads, ...orphanDocs].sort((a, b) => toMillis(b?.createdAt) - toMillis(a?.createdAt));
  }, [orphanDocs, portalUploads]);

  const loading = orphansLoading || portalLoading;

  const selectedDoc = useMemo(() => {
    const id = String(selectedDocId || '').trim();
    if (!id) return null;
    return (Array.isArray(docs) ? docs : []).find((d) => String(d?.id || '') === id) || null;
  }, [docs, selectedDocId]);

  const resolveEntityArchiveContext = useCallback(async (entity) => {
    const entityId = readText(entity?.id);
    const entityType = readText(entity?.type);
    const entityName = readText(entity?.name);

    if (!entityId || !entityType) {
      throw new Error('Destino jurídico inválido para arquivamento.');
    }

    if (entityType === 'client') {
      const clientData = await repo.fetchClientById(appId, entityId);
      const clientName = readText(entityName || clientData?.qualification?.physical?.name || clientData?.qualification?.legal?.companyName || clientData?.anchor?.name || clientData?.name || 'Cliente');
      return {
        clientId: entityId,
        clientName,
        matterId: '',
        matterName: '',
        clientData,
        matterData: null,
      };
    }

    if (entityType !== 'matter') {
      throw new Error('Tipo de destino jurídico não suportado.');
    }

    const matterData = await repo.fetchMatterById(appId, entityId);
    const clientId = readText(matterData?.clientId || matterData?.clientRef || matterData?.client);
    if (!clientId) {
      throw new Error('O processo selecionado não possui clientId vinculado.');
    }

    const clientData = await repo.fetchClientById(appId, clientId);
    const clientName = readText(matterData?.clientName || clientData?.qualification?.physical?.name || clientData?.qualification?.legal?.companyName || clientData?.anchor?.name || clientData?.name || 'Cliente');
    const matterName = readText(entityName || resolveMatterLabel(matterData));

    return {
      clientId,
      clientName,
      matterId: entityId,
      matterName,
      clientData,
      matterData,
    };
  }, []);

  const ensureMatterWorkspace = useCallback(async (context) => {
    const matterId = readText(context?.matterId);
    if (!matterId) return null;

    const clientId = readText(context?.clientId);
    const clientName = readText(context?.clientName);
    const clientData = context?.clientData && typeof context.clientData === 'object' ? context.clientData : {};
    const matterData = context?.matterData && typeof context.matterData === 'object' ? context.matterData : {};

    // [P73+] Busca o workspace primeiro — se já tiver IDs de pasta, retorna sem acionar Drive API.
    const { workspaceDoc, workspaceRef, workspaceData } = await repo.findWorkspaceByMatterId(appId, matterId);
    if (readText(workspaceData?.processPieceFolderId) && readText(workspaceData?.attachmentsFolderId)) {
      return { id: workspaceRef.id, ...workspaceData };
    }

    let driveFolders = clientData?.driveFolders && typeof clientData.driveFolders === 'object' ? clientData.driveFolders : null;
    const rootFolderId = readText(clientData?.driveFolderId || clientData?.drive?.rootFolderId || driveFolders?.root);
    let processRootFolderId = readText(driveFolders?.processos || clientData?.drive?.processosFolderId);

    if ((!processRootFolderId || shouldRunDriveAutoHealFromClientSnapshot(clientData)) && rootFolderId) {
      const reconcileResp = await fetch(`${getBackendBaseUrl()}/drive/reconcile-client-tree`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rootFolderId,
          clientId,
          clientName,
        }),
      });
      const reconcilePayload = await reconcileResp.json().catch(() => ({}));
      if (!reconcileResp.ok) {
        throw new Error(String(reconcilePayload?.detail || reconcilePayload?.error || 'Falha ao reconciliar a árvore do cliente no Drive.'));
      }

      driveFolders = reconcilePayload?.driveFolders && typeof reconcilePayload.driveFolders === 'object'
        ? reconcilePayload.driveFolders
        : driveFolders;
      processRootFolderId = readText(driveFolders?.processos || processRootFolderId);

      await repo.updateClientDriveData(appId, clientId, {
        driveFolderId: rootFolderId,
        driveFolders: driveFolders || null,
        driveTreeVersion: DRIVE_TREE_VERSION,
        driveTreeHealthy: true,
      });
    }

    if (!processRootFolderId) {
      throw new Error('A pasta estrutural Caderno de Autos não está disponível para este cliente/processo.');
    }

    const pieceType = readText(workspaceData?.pieceType || context?.matterName || resolveMatterLabel(matterData));

    const ensureResp = await fetch(`${getBackendBaseUrl()}/drive/ensure-workspace-folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        processRootFolderId,
        workspaceId: workspaceRef.id,
        pieceType,
        clientId,
        clientName,
      }),
    });
    const ensurePayload = await ensureResp.json().catch(() => ({}));
    if (!ensureResp.ok) {
      throw new Error(String(ensurePayload?.detail || ensurePayload?.error || 'Falha ao garantir as pastas do caderno de autos.'));
    }

    const processPieceFolderId = readText(ensurePayload?.pieceFolder?.id);
    const attachmentsFolderId = readText(ensurePayload?.attachmentsFolder?.id);
    if (!processPieceFolderId || !attachmentsFolderId) {
      throw new Error('Backend não retornou a subárvore peça/anexos esperada.');
    }

    const workspacePayload = {
      clientId,
      clientName,
      matterId,
      matterName: readText(context?.matterName || resolveMatterLabel(matterData)) || null,
      pieceType,
      processRootFolderId,
      processPieceFolderId,
      attachmentsFolderId,
      driveFolderId: attachmentsFolderId,
      driveFolderInput: attachmentsFolderId,
      updatedAt: repo.serverTimestamp(),
    };

    if (!workspaceDoc) {
      workspacePayload.createdAt = repo.serverTimestamp();
    }

    await repo.upsertWorkspace(workspaceRef, workspacePayload, !workspaceDoc);
    return { id: workspaceRef.id, ...workspaceData, ...workspacePayload };
  }, []);

  const moveDriveFileIfNeeded = useCallback(async (row, targetFolderId, desiredName = '') => {
    const storageEngine = row?.storageEngine && typeof row.storageEngine === 'object' ? row.storageEngine : null;
    const provider = readText(storageEngine?.provider).toLowerCase();
    const fileId = readText(storageEngine?.fileId || storageEngine?.id);
    if (provider !== 'gdrive' || !fileId || !targetFolderId) {
      return {
        fileUrl: resolveFileUrl(row),
        fileName: resolveFileName(row),
        storageEngine: storageEngine || null,
      };
    }

    const moveResp = await fetch(`${getBackendBaseUrl()}/drive/move-file`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileId,
        targetFolderId,
        desiredName: readText(desiredName) || undefined,
      }),
    });
    const movePayload = await moveResp.json().catch(() => ({}));
    if (!moveResp.ok) {
      throw new Error(String(movePayload?.detail || movePayload?.error || 'Falha ao mover arquivo no Google Drive.'));
    }

    return {
      fileUrl: readText(movePayload?.webViewLink || movePayload?.webContentLink || resolveFileUrl(row)),
      fileName: readText(movePayload?.name || resolveFileName(row)),
      storageEngine: {
        ...(storageEngine || {}),
        provider: 'gdrive',
        fileId: readText(movePayload?.fileId || fileId),
        id: readText(movePayload?.fileId || fileId),
        parents: Array.isArray(movePayload?.parents) ? movePayload.parents : (storageEngine?.parents || []),
        previousParents: Array.isArray(movePayload?.previousParents) ? movePayload.previousParents : (storageEngine?.previousParents || []),
        webViewLink: readText(movePayload?.webViewLink || storageEngine?.webViewLink) || null,
        webContentLink: readText(movePayload?.webContentLink || storageEngine?.webContentLink) || null,
        name: readText(movePayload?.name || storageEngine?.name || resolveFileName(row)) || null,
      },
    };
  }, []);

  const archiveIntoGed = useCallback(async ({ row, entity, sourceDocId, sourceKind }) => {
    const context = await resolveEntityArchiveContext(entity);
    const workspace = context?.matterId ? await ensureMatterWorkspace(context) : null;
    const desiredArchiveName = buildArchiveFileName({ row, context, sourceKind });
    const moved = await moveDriveFileIfNeeded(row, readText(workspace?.attachmentsFolderId), desiredArchiveName);
    const movedStorageEngine = moved?.storageEngine && typeof moved.storageEngine === 'object' ? moved.storageEngine : null;
    const movedFileId = readText(movedStorageEngine?.fileId || movedStorageEngine?.id);
    const previousParents = Array.isArray(movedStorageEngine?.previousParents)
      ? movedStorageEngine.previousParents.map((parent) => readText(parent)).filter(Boolean)
      : [];
    const fileUrl = readText(moved?.fileUrl || resolveFileUrl(row));
    const fileName = readText(moved?.fileName || resolveFileName(row));
    if (!fileUrl) {
      throw new Error('Arquivo sem URL válida para arquivamento.');
    }

    const triageRef = sourceKind === 'portal_upload'
      ? (row?._ref || repo.getTriageRef(readText(sourceDocId)))
      : null;

    const documentPayload = {
      fileUrl,
      fileName,
      title: fileName,
      name: fileName,
      clientId: context.clientId || '',
      clientName: context.clientName || null,
      matterId: context.matterId || '',
      matterName: context.matterName || null,
      source: sourceKind === 'portal_upload' ? 'portal' : 'triage',
      sourceKind: sourceKind || null,
      sourceRefId: sourceDocId || null,
      status: 'VINCULADO',
      createdAt: repo.serverTimestamp(),
      updatedAt: repo.serverTimestamp(),
      relatedEntities: [
        { id: context.clientId, type: 'client', name: context.clientName || null },
        ...(context.matterId ? [{ id: context.matterId, type: 'matter', name: context.matterName || null }] : [])
      ],
      context: {
        relatedEntities: [context.clientId, ...(context.matterId ? [context.matterId] : [])],
        primaryEntityId: context.matterId || context.clientId,
        primaryEntityName: context.matterName || context.clientName || 'Vinculado',
      },
      mimeType: row?.mimeType || moved?.storageEngine?.mimeType || null,
      storageEngine: moved?.storageEngine || row?.storageEngine || null,
      classification: row?.classification || null,
      category: row?.category || row?.classification?.category || null,
      driveWorkspaceId: workspace?.id || null,
      cadernoId: workspace?.id || null,
      cadernoTitle: String(workspace?.pieceType || workspace?.title || '').trim() || null,
      processRootFolderId: workspace?.processRootFolderId || null,
      processPieceFolderId: workspace?.processPieceFolderId || null,
      attachmentsFolderId: workspace?.attachmentsFolderId || null,
    };

    const buildBatch = (gedCollectionRef) => {
      const batch = repo.createBatch();
      const gedRef = repo.docFromCollection(gedCollectionRef);
      batch.set(gedRef, documentPayload);

      if (triageRef) {
        batch.update(triageRef, {
          status: 'approved',
          updatedAt: repo.serverTimestamp(),
          documentId: gedRef.id,
          clientId: context.clientId || null,
          clientName: context.clientName || null,
          matterId: context.matterId || null,
          matterName: context.matterName || null,
          workspaceId: workspace?.id || null,
          archivedFolderId: workspace?.attachmentsFolderId || null,
          fileUrl,
          fileName,
          storageEngine: moved?.storageEngine || row?.storageEngine || null,
        });
      }

      return { batch, documentId: gedRef.id };
    };

    const hasAppId = readText(appId);
    let firstError = null;
    let archivedDocumentId = '';

    if (hasAppId) {
      try {
        const artifactsDocs = repo.getArtifactsDocumentsCollection(hasAppId);
        const artifactsWrite = buildBatch(artifactsDocs);
        await artifactsWrite.batch.commit();
        archivedDocumentId = readText(artifactsWrite.documentId);
      } catch (e) {
        firstError = e;
      }
    }

    if (!archivedDocumentId) {
      try {
        const rootWrite = buildBatch(repo.getRootDocumentsCollection());
        await rootWrite.batch.commit();
        archivedDocumentId = readText(rootWrite.documentId);
      } catch (e) {
        if (movedFileId && previousParents.length) {
          try {
            await moveDriveFile({
              fileId: movedFileId,
              targetFolderId: previousParents[0],
              desiredName: resolveFileName(row),
            });
          } catch (rollbackError) {
            const primaryMessage = firstError
              ? `Falha ao arquivar. (artifacts): ${String(firstError?.message || firstError)}\n(raiz): ${String(e?.message || e)}`
              : String(e?.message || e || 'Falha ao arquivar documento.');
            throw new Error(`${primaryMessage}\n(rollback-drive): ${String(rollbackError?.message || rollbackError || 'Falha ao reverter arquivo no Drive.')}`);
          }
        }
        if (firstError) {
          throw new Error(`Falha ao arquivar. (artifacts): ${String(firstError?.message || firstError)}\n(raiz): ${String(e?.message || e)}`);
        }
        throw e;
      }
    }

    if (sourceKind === 'document_orphan' && sourceDocId) {
      await repo.updateDocumentVinculo(appId, sourceDocId, {
        clientId: context.clientId || null,
        clientName: context.clientName || null,
        matterId: context.matterId || null,
        matterName: context.matterName || null,
        status: 'VINCULADO',
        relatedEntities: repo.arrayUnion(
          { id: context.clientId, type: 'client', name: context.clientName || null },
          ...(context.matterId ? [{ id: context.matterId, type: 'matter', name: context.matterName || null }] : [])
        ),
        'context.relatedEntities': repo.arrayUnion(context.clientId, ...(context.matterId ? [context.matterId] : [])),
        'context.primaryEntityId': context.matterId || context.clientId,
        'context.primaryEntityName': context.matterName || context.clientName || 'Vinculado',
        driveWorkspaceId: workspace?.id || null,
        processRootFolderId: workspace?.processRootFolderId || null,
        processPieceFolderId: workspace?.processPieceFolderId || null,
        attachmentsFolderId: workspace?.attachmentsFolderId || null,
        fileUrl,
        fileName,
        storageEngine: moved?.storageEngine || row?.storageEngine || null,
      });
    }

    await writeClientNote({
      clientId: context.clientId,
      type: sourceKind === 'portal_upload' ? 'PORTAL_UPLOAD_APPROVED' : 'DOC_UPLOADED',
      actorName: sourceKind === 'portal_upload' ? 'Escritório' : 'Escritório',
      payload: {
        documentId: archivedDocumentId || null,
        fileName,
        fileUrl,
        workspaceId: workspace?.id || null,
        attachmentsFolderId: workspace?.attachmentsFolderId || null,
        linkedTo: {
          id: context.matterId || context.clientId,
          type: context.matterId ? 'matter' : 'client',
          name: context.matterName || context.clientName || null,
        },
      },
    });

    return {
      documentId: archivedDocumentId,
      workspace,
      context,
    };
  }, [ensureMatterWorkspace, moveDriveFileIfNeeded, resolveEntityArchiveContext]);

  useEffect(() => {
    if (reloadTrigger > 0) { setActionError(''); loadOrphans(); }
  }, [reloadTrigger]);

  const loadOrphans = useCallback(async () => {
    setOrphansLoading(true);
    setError('');
    setNotice('');

    try {
      const rows = await repo.loadOrphanDocuments(appId);
      setOrphanDocs(rows);
      setSelectedEntity(null);
    } catch (e) {
      setOrphanDocs([]);
      setError(String(e?.message || e));
    } finally {
      setOrphansLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrphans();
  }, [loadOrphans]);

  useEffect(() => {
    setPortalLoading(true);
    return repo.subscribePortalUploads(
      appId,
      (rows) => {
        setPortalUploads(rows);
        setPortalLoading(false);
      },
      (msg) => {
        setPortalUploads([]);
        setPortalLoading(false);
        setError(String(msg || 'Falha ao carregar a fila do portal.'));
      }
    );
  }, []);

  useEffect(() => {
    setSelectedDocId((cur) => {
      const curId = String(cur || '').trim();
      if (curId && docs.some((row) => String(row?.id || '') === curId)) return curId;
      return docs[0]?.id ? String(docs[0].id) : '';
    });
  }, [docs]);

  const handlePickDoc = useCallback(
    (row) => {
      const id = String(row?.id || '').trim();
      if (!id) return;
      setSelectedDocId(id);
      setSelectedEntity(null);
      setNotice('');
      setError('');
    },
    []
  );

  const handleLink = useCallback(async () => {
    const docId = String(selectedDoc?.sourceRefId || '').trim();
    const entityId = String(selectedEntity?.id || '').trim();
    const entityType = String(selectedEntity?.type || '').trim();

    if (!docId || !entityId || !entityType) return;
    if (String(selectedDoc?.sourceKind || '') !== 'document_orphan') return;
    if (linking) return;

    setLinking(true);
    setError('');
    setNotice('');

    try {
      await archiveIntoGed({
        row: selectedDoc,
        entity: selectedEntity,
        sourceDocId: docId,
        sourceKind: 'document_orphan',
      });

      setOrphanDocs((prev) => (Array.isArray(prev) ? prev.filter((d) => String(d?.id || '') !== String(selectedDoc?.id || '')) : []));
      setSelectedEntity(null);

      setNotice('Vínculo salvo. Documento arquivado da triagem.');
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLinking(false);
    }
  }, [archiveIntoGed, linking, selectedDoc, selectedEntity]);

  const handleApprovePortal = useCallback(async (row = selectedDoc) => {
    const triageId = String(row?.sourceRefId || '').trim();
    if (!triageId) return;
    if (workingId) return;

    const entityId = String(selectedEntity?.id || row?.clientId || '').trim();
    const entityType = String(selectedEntity?.type || (row?.clientId ? 'client' : '')).trim();
    const entityName = String(selectedEntity?.name || row?.clientName || '').trim();
    const taskRef = String(row?.taskRef || '').trim();

    if (!entityId || !entityType) {
      setActionError('Aprovação falhou: selecione um cliente ou processo para arquivamento.');
      return;
    }

    setActionError('');
    setWorkingId(String(row?.id || '').trim());
    setWorkingAction('approve');

    try {
      const archived = await archiveIntoGed({
        row,
        entity: { id: entityId, type: entityType, name: entityName || null },
        sourceDocId: triageId,
        sourceKind: 'portal_upload',
      });

      if (taskRef) {
        await repo.updateDocumentRequest(taskRef, {
          status: 'approved',
          documentId: archived?.documentId || null,
          workspaceId: archived?.workspace?.id || null,
        });
      }

      setSelectedEntity(null);
      setNotice('Upload do portal aprovado e arquivado no GED.');
    } catch (e) {
      setActionError(String(e?.message || e || 'Falha ao aprovar.'));
    } finally {
      setWorkingId('');
      setWorkingAction('');
    }
  }, [archiveIntoGed, selectedDoc, selectedEntity, workingId]);

  const handleRejectPortal = useCallback(async (row = selectedDoc) => {
    const triageId = String(row?.sourceRefId || '').trim();
    if (!triageId) return;
    if (workingId) return;

    const taskRef = String(row?.taskRef || '').trim();
    const reason = window.prompt('Motivo da Rejeição', '');
    if (reason === null) return;
    const cleanReason = String(reason || '').trim();
    if (!cleanReason) return;

    const clientId = String(row?.clientId || '').trim();
    const fileName = resolveFileName(row);
    const fileUrl = resolveFileUrl(row);

    setActionError('');
    setWorkingId(String(row?.id || '').trim());
    setWorkingAction('reject');

    try {
      const triageRef = row?._ref || repo.getTriageRef(triageId);
      await repo.rejectPortalUpload({ triageRef, taskRef, cleanReason });

      try {
        await writeClientNote({
          clientId,
          type: 'PORTAL_UPLOAD_REJECTED',
          actorName: 'Escritório',
          payload: {
            fileName,
            fileUrl,
            taskRef: taskRef || null,
            rejectionReason: cleanReason,
            text: `Arquivo devolvido para novo envio: ${fileName}`,
          },
        });
      } catch (e) {
        setActionError(`Devolução concluída, mas falhou ao registrar histórico: ${String(e?.message || e)}`);
      }

      setNotice('Upload do portal devolvido ao cliente para novo envio.');
    } catch (e) {
      setActionError(String(e?.message || e || 'Falha ao rejeitar.'));
    } finally {
      setWorkingId('');
      setWorkingAction('');
    }
  }, [selectedDoc, workingId]);

  const safeTitle = useMemo(() => {
    return resolveFileName(selectedDoc);
  }, [selectedDoc]);

  const safeFileUrl = useMemo(() => {
    return String(resolvedFileUrl || '').trim();
  }, [resolvedFileUrl]);

  const safeCategory = useMemo(() => {
    return String(
      selectedDoc?.sourceKind === 'portal_upload'
        ? 'PORTAL_UPLOAD'
        : selectedDoc?.classification?.category || selectedDoc?.category || ''
    ).trim() || 'TRIAGEM';
  }, [selectedDoc?.classification?.category, selectedDoc?.category]);

  const isPortalUpload = useMemo(() => String(selectedDoc?.sourceKind || '') === 'portal_upload', [selectedDoc?.sourceKind]);

  const isWhatsAppTriage = useMemo(() => safeCategory === 'TRIAGEM_WHATSAPP', [safeCategory]);

  const whatsAppSummary = useMemo(() => {
    return String(selectedDoc?.summary || '').trim();
  }, [selectedDoc?.summary]);

  const whatsAppMetrics = useMemo(() => {
    const result = selectedDoc?.whatsappResult && typeof selectedDoc.whatsappResult === 'object' ? selectedDoc.whatsappResult : {};
    return {
      mediaCount: Number(result?.mediaCount || 0),
      orphansCount: Number(result?.orphansCount || 0),
      technicalReportPath: String(result?.technicalReportPath || '').trim(),
      messagePreview: Array.isArray(result?.messagePreview) ? result.messagePreview : [],
    };
  }, [selectedDoc?.whatsappResult]);

  useEffect(() => {
    setAiResult(null);
    setAiError('');
    setAiLoading(false);
  }, [selectedDocId]);

  useEffect(() => {
    let mounted = true;

    const resolveUrl = async () => {
      if (!selectedDoc) {
        setResolvedFileUrl('');
        setPreviewError('');
        setPreviewLoading(false);
        return;
      }

      if (isWhatsAppTriage) {
        setResolvedFileUrl('');
        setPreviewError('');
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);
      setPreviewError('');
      setResolvedFileUrl('');

      try {
        const url = await resolveDocumentUrl(selectedDoc);
        if (!mounted) return;
        const nextUrl = String(url || '').trim();
        setResolvedFileUrl(nextUrl);
        if (!nextUrl && !isWhatsAppTriage) {
          setPreviewError('Pré-visualização indisponível para este item.');
        }
      } catch (e) {
        if (!mounted) return;
        setPreviewError(String(e?.message || e || 'Falha ao resolver a pré-visualização.'));
      } finally {
        if (mounted) setPreviewLoading(false);
      }
    };

    resolveUrl();

    return () => {
      mounted = false;
    };
  }, [isWhatsAppTriage, selectedDoc]);

  const handleAnalyzeDocument = useCallback(async () => {
    const fileSource = String(resolvedFileUrl || '').trim();
    const mimeType = String(selectedDoc?.mimeType || '').trim();
    if (!fileSource || aiLoading) return;

    setAiLoading(true);
    setAiError('');
    setAiResult(null);

    try {
      const result = await extractDocumentData(fileSource, mimeType);
      if (!result?.success || String(result?.quality || '').toUpperCase() === 'BLURRED') {
        const reason = String(result?.reason || 'Imagem sem nitidez para extração segura.').trim();
        setAiError(reason);
        alert(reason);
        return;
      }

      setAiResult(result);
    } catch (e) {
      setAiError(String(e?.message || e || 'Falha ao analisar documento.'));
    } finally {
      setAiLoading(false);
    }
  }, [aiLoading, resolvedFileUrl, selectedDoc?.mimeType]);

  const handleCreateClientFromAI = useCallback(() => {
    const extractedData = aiResult?.extractedData && typeof aiResult.extractedData === 'object' ? aiResult.extractedData : null;
    if (!extractedData) return;
    const payload = {
      prefillData: extractedData,
      sourceDocumentId: String(selectedDoc?.id || '').trim(),
    };

    if (typeof onCreateClientFromAI === 'function') {
      onCreateClientFromAI(extractedData);
      return;
    }

    const pushed = pushClientPrefillToBrowserState(payload);
    if (pushed) {
      try {
        window.dispatchEvent(new CustomEvent('rda:client-prefill', { detail: { prefillData: extractedData } }));
      } catch {
        void 0;
      }
    }

  }, [aiResult?.extractedData, onCreateClientFromAI, selectedDoc?.id]);

  const handleWhatsAppImported = useCallback(async (payload, sourcePath, linkedEntity) => {
    const messages = Array.isArray(payload?.messages) ? payload.messages : [];
    const summary = String(payload?.executive_summary || '').trim();
    const technicalReportPath = String(payload?.technical_report_path || '').trim();
    const mediaCount = Number(payload?.media_count || 0);
    const orphansCount = Number(payload?.orphans_count || 0);
    const safeSourcePath = String(sourcePath || '').trim();
    const entityId = String(linkedEntity?.id || '').trim();
    const entityType = String(linkedEntity?.type || '').trim();
    const entityName = String(linkedEntity?.name || '').trim();

    if (!entityId || !entityType) {
      throw new Error('Vínculo jurídico ausente para a triagem de WhatsApp.');
    }

    const whatsappFile = buildWhatsAppJsonFile({
      payload,
      sourcePath,
      linkedEntity: { id: entityId, type: entityType, name: entityName || null },
    });
    let uploadedFileId = '';
    let storageEngine = null;

    try {
      const uploadResult = await uploadToBackendDrive(whatsappFile, whatsappFile.name);
      storageEngine = uploadResult?.storageEngineData && typeof uploadResult.storageEngineData === 'object'
        ? uploadResult.storageEngineData
        : null;
      uploadedFileId = readText(storageEngine?.fileId || storageEngine?.id);
      if (!uploadedFileId) {
        throw new Error('Google Drive não retornou fileId para o dossiê JSON do WhatsApp.');
      }
    } catch (e) {
      throw new Error(`Falha ao carregar dossiê JSON do WhatsApp no Google Drive: ${String(e?.message || e)}`);
    }

    const docPayload = {
      title: `Triagem WhatsApp - ${new Date().toLocaleString()}`,
      name: whatsappFile.name,
      category: 'TRIAGEM_WHATSAPP',
      status: 'VINCULADO',
      source: 'WHATSAPP_IMPORT',
      mimeType: 'application/json',
      fileName: whatsappFile.name,
      fileUrl: readText(storageEngine?.webViewLink || storageEngine?.webContentLink),
      createdAt: repo.serverTimestamp(),
      updatedAt: repo.serverTimestamp(),
      clientId: entityType === 'client' ? entityId : '',
      matterId: entityType === 'matter' ? entityId : '',
      relatedEntities: [{ id: entityId, type: entityType, name: entityName || null }],
      classification: {
        category: 'TRIAGEM_WHATSAPP',
        confidence: 0.95,
        source: 'WHATSAPP_IMPORT',
      },
      tags: ['whatsapp', 'triagem', 'forense'],
      localSourcePath: safeSourcePath,
      summary,
      storageEngine,
      context: {
        relatedEntities: [entityId],
        primaryEntityId: entityId,
        primaryEntityName: entityName || 'Vinculado',
      },
      whatsappResult: {
        mediaCount,
        orphansCount,
        technicalReportPath,
        messagePreview: messages.slice(0, 50),
      },
    };

    try {
      await repo.addWhatsAppDocument(appId, docPayload);
      setNotice(`Triagem de WhatsApp importada e vinculada a ${entityType === 'client' ? 'cliente' : 'processo'} com sucesso.`);
      await loadOrphans();
    } catch (e) {
      await deleteDriveFileSilently(uploadedFileId);
      throw e;
    }
  }, [loadOrphans]);

  const handleWhatsAppImportedUnlinked = useCallback(async (payload, sourcePath) => {
    const messages = Array.isArray(payload?.messages) ? payload.messages : [];
    const summary = String(payload?.executive_summary || '').trim();
    const technicalReportPath = String(payload?.technical_report_path || '').trim();
    const mediaCount = Number(payload?.media_count || 0);
    const orphansCount = Number(payload?.orphans_count || 0);
    const safeSourcePath = String(sourcePath || '').trim();

    const whatsappFile = buildWhatsAppJsonFile({
      payload,
      sourcePath,
      linkedEntity: { id: '', type: 'unlinked', name: 'Sem Vínculo' },
    });

    let uploadedFileId = '';
    let storageEngine = null;

    try {
      const uploadResult = await uploadToBackendDrive(whatsappFile, whatsappFile.name);
      storageEngine = uploadResult?.storageEngineData && typeof uploadResult.storageEngineData === 'object'
        ? uploadResult.storageEngineData
        : null;
      uploadedFileId = readText(storageEngine?.fileId || storageEngine?.id);
      if (!uploadedFileId) {
        throw new Error('Google Drive não retornou fileId para o dossiê JSON do WhatsApp.');
      }
    } catch (e) {
      throw new Error(`Falha ao carregar dossiê JSON do WhatsApp no Google Drive: ${String(e?.message || e)}`);
    }

    const docPayload = {
      title: `Triagem WhatsApp (Sem vínculo) - ${new Date().toLocaleString()}`,
      name: whatsappFile.name,
      category: 'TRIAGEM_WHATSAPP',
      status: 'TRIAGEM',
      source: 'WHATSAPP_IMPORT',
      mimeType: 'application/json',
      fileName: whatsappFile.name,
      fileUrl: readText(storageEngine?.webViewLink || storageEngine?.webContentLink),
      createdAt: repo.serverTimestamp(),
      updatedAt: repo.serverTimestamp(),
      clientId: '',
      matterId: '',
      relatedEntities: [],
      classification: {
        category: 'TRIAGEM_WHATSAPP',
        confidence: 0.95,
        source: 'WHATSAPP_IMPORT',
      },
      tags: ['whatsapp', 'triagem', 'forense', 'sem_vinculo'],
      localSourcePath: safeSourcePath,
      summary,
      storageEngine,
      context: {
        relatedEntities: [],
        primaryEntityId: '',
        primaryEntityName: 'Sem vínculo',
      },
      whatsappResult: {
        mediaCount,
        orphansCount,
        technicalReportPath,
        messagePreview: messages.slice(0, 50),
      },
    };

    try {
      await repo.addWhatsAppDocument(appId, docPayload);
      setNotice('Triagem de WhatsApp importada sem vínculo (modo teste).');
      await loadOrphans();
    } catch (e) {
      await deleteDriveFileSilently(uploadedFileId);
      throw e;
    }
  }, [loadOrphans]);

  const handleWhatsAppProcessed = useCallback((payload, sourcePath) => {
    const messages = Array.isArray(payload?.messages) ? payload.messages : [];
    setWhatsAppDraft({
      payload,
      sourcePath: String(sourcePath || '').trim(),
      summary: String(payload?.executive_summary || '').trim(),
      technicalReportPath: String(payload?.technical_report_path || '').trim(),
      outputBase: String(payload?.output_base || payload?.outputBase || '').trim(),
      capabilities: payload?.capabilities && typeof payload.capabilities === 'object' ? payload.capabilities : null,
      mediaCount: Number(payload?.media_count || 0),
      orphansCount: Number(payload?.orphans_count || 0),
      messagePreview: messages.slice(0, 40),
    });
    setNotice('Parser concluído. Agora vincule o dossiê a um cliente ou processo para arquivar no GED.');
    setError('');
  }, []);

  return (
    <div data-testid="ingestion-hub" className="h-full w-full flex flex-col bg-gray-50">
      <span data-testid="ingestion-title" className="sr-only">{pageTitle}</span>

      {error ? (
        <div className="px-6 py-3 bg-red-50 border-b border-red-100 text-red-700 text-xs font-bold">{error}</div>
      ) : null}

      {actionError ? (
        <div className="px-6 py-3 bg-red-50 border-b border-red-100 text-red-700 text-xs font-bold whitespace-pre-wrap">{actionError}</div>
      ) : null}

      {notice ? (
        <div className="px-6 py-3 bg-emerald-50 border-b border-emerald-100 text-emerald-800 text-xs font-bold">{notice}</div>
      ) : null}

      {isWhatsAppMode ? (
        <div data-testid="ingestion-layout" className="flex-1 min-h-0 px-8 py-6 overflow-y-auto custom-scrollbar">
          <div className="w-full space-y-6">
            <div className="bg-white border border-gray-300 rounded-xl shadow-sm p-6 lg:p-8">
              <div className="w-full">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">Edge Computing · Porta 8009</div>
                <div className="mt-2 text-2xl font-extrabold text-[#20243B]">Parser Forense de WhatsApp</div>
                <div className="mt-2 text-sm text-slate-600">Selecione um arquivo ZIP exportado do WhatsApp (ou uma pasta) para processar o dossiê no backend Edge antes de qualquer vínculo jurídico.</div>
              </div>

              <div className="mt-6">
                <WhatsAppImporter onProcessed={handleWhatsAppProcessed} onImported={handleWhatsAppImported} linkedEntity={whatsAppTargetEntity} allowUnlinked />
              </div>
            </div>

            {whatsAppDraft ? (
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                <section className="xl:col-span-7 rounded-xl border border-gray-300 bg-white shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-300 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-extrabold uppercase tracking-widest text-gray-600">Dossiê Processado</div>
                      <div className="mt-1 text-[11px] font-bold text-slate-500">Resultado técnico do parser antes do arquivamento jurídico.</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                      <span className="px-2 py-1 rounded bg-slate-100">Mensagens: {whatsAppDraft.messagePreview.length}</span>
                      <span className="px-2 py-1 rounded bg-slate-100">Mídias: {whatsAppDraft.mediaCount}</span>
                      <span className="px-2 py-1 rounded bg-slate-100">Órfãos: {whatsAppDraft.orphansCount}</span>
                    </div>
                  </div>

                  <div className="p-6 space-y-5">
                    <div>
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Resumo Executivo</div>
                      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap">{whatsAppDraft.summary || 'Resumo executivo indisponível.'}</div>
                    </div>

                    <div>
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Relatório Técnico Local</div>
                      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-mono text-slate-600 break-all">{whatsAppDraft.technicalReportPath || whatsAppDraft.outputBase || whatsAppDraft.sourcePath || 'Caminho técnico não informado.'}</div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const base = String(whatsAppDraft?.outputBase || '').trim();
                            const tech = String(whatsAppDraft?.technicalReportPath || '').trim();
                            if (base) return openPathOnSystem(base);
                            if (tech) {
                              const slash = Math.max(tech.lastIndexOf('\\'), tech.lastIndexOf('/'));
                              const folder = slash > 0 ? tech.slice(0, slash) : tech;
                              return openPathOnSystem(folder);
                            }
                            return openPathOnSystem(whatsAppDraft?.sourcePath);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-[10px] font-extrabold uppercase tracking-widest hover:bg-slate-50"
                        >
                          <ExternalLink size={14} />
                          Abrir Pasta
                        </button>

                        <button
                          type="button"
                          onClick={() => openPathOnSystem(whatsAppDraft?.technicalReportPath)}
                          disabled={!String(whatsAppDraft?.technicalReportPath || '').trim()}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-[10px] font-extrabold uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50"
                        >
                          <ExternalLink size={14} />
                          Abrir Relatório
                        </button>
                      </div>

                      {(() => {
                        const caps = whatsAppDraft?.capabilities && typeof whatsAppDraft.capabilities === 'object' ? whatsAppDraft.capabilities : null;
                        if (!caps) return null;
                        const whisperOk = caps?.whisper_available !== false;
                        const ffmpegOk = caps?.ffmpeg_available !== false;
                        if (whisperOk && ffmpegOk) return null;
                        return (
                          <div className="mt-2">
                            <span className="text-[10px] text-slate-400">Transcrição de áudio indisponível. Textos funcionam normalmente.</span>
                          </div>
                        );
                      })()}
                    </div>

                    <div>
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">Prévia das Mensagens Importadas</div>
                      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <div className="max-h-[360px] overflow-y-auto custom-scrollbar divide-y divide-slate-100">
                          {whatsAppDraft.messagePreview.length ? whatsAppDraft.messagePreview.map((row, index) => (
                            <div key={`${row?.index || index}-${row?.timestamp || ''}`} className="px-4 py-3">
                              <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                                <span>{row?.timestamp || 'Sem data'}</span>
                                <span>•</span>
                                <span>{row?.author || 'Sem autor'}</span>
                                {row?.linked_file ? <><span>•</span><span>{row.linked_file}</span></> : null}
                              </div>
                              <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{String(row?.text || '—').trim() || '—'}</div>
                            </div>
                          )) : (
                            <div className="px-4 py-6 text-sm text-slate-400">Nenhuma mensagem disponível neste processamento.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <aside className="xl:col-span-5 rounded-xl border border-gray-300 bg-white shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-300">
                    <div className="text-xs font-extrabold uppercase tracking-widest text-[#20243B]">Destino Jurídico</div>
                    <div className="mt-1 text-[11px] font-bold text-slate-500">Após o parser concluir, vincule o dossiê a um cliente ou processo para arquivar no GED.</div>
                  </div>

                  <div className="p-6 space-y-4">
                    <SmartEntityLinker
                      placeholder="Buscar cliente ou processo para arquivar o dossiê..."
                      onSelect={(entity) => {
                        if (!entity?.id) return;
                        setWhatsAppTargetEntity({
                          id: entity.id,
                          type: entity.type,
                          name: entity.name,
                          raw: entity.raw,
                        });
                        setNotice('');
                        setError('');
                      }}
                    />

                    {whatsAppTargetEntity ? (
                      <>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 flex items-start justify-between gap-3">
                          <div className="text-[11px] text-emerald-900 font-bold min-w-0">
                            <div className="truncate" title={toTitleCasePt(whatsAppTargetEntity.name)}>{toTitleCasePt(whatsAppTargetEntity.name)}</div>
                            <div className="mt-1 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">{whatsAppTargetEntity.type === 'client' ? 'CLIENTE' : 'PROCESSO'}</div>
                          </div>
                          <button type="button" onClick={() => setWhatsAppTargetEntity(null)} className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-800 hover:text-red-600">Limpar</button>
                        </div>

                        <div className="rounded-xl border border-gray-300 bg-gray-50 px-4 py-4">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-700">Arquivamento Jurídico</div>
                          <div className="mt-2 text-xs text-slate-600">Com o destino jurídico definido, confirme o arquivamento do dossiê processado no GED.</div>
                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleWhatsAppImported(whatsAppDraft.payload, whatsAppDraft.sourcePath, whatsAppTargetEntity)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#20243B] text-white text-xs font-extrabold uppercase tracking-widest hover:opacity-95"
                            >
                              <Archive size={14} />
                              Arquivar Dossiê no GED
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">Selecione o destino jurídico somente após revisar o resultado do parser.</div>
                        <div className="rounded-xl border border-gray-300 bg-gray-50 px-4 py-4">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-700">Modo Teste (Sem vínculo)</div>
                          <div className="mt-2 text-xs text-slate-600">Se você ainda não quiser vincular ao cliente/processo, arquive o dossiê no GED como triagem sem vínculo.</div>
                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleWhatsAppImportedUnlinked(whatsAppDraft.payload, whatsAppDraft.sourcePath)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#20243B] text-white text-xs font-extrabold uppercase tracking-widest hover:opacity-95"
                            >
                              <Archive size={14} />
                              Arquivar Sem Vínculo
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </aside>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
      <div data-testid="ingestion-layout" className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-0 border border-gray-300 rounded-xl overflow-hidden">
        <aside data-testid="ingestion-list" className="lg:col-span-4 border-r border-gray-300 bg-white min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="p-6 text-center text-xs text-gray-400">Carregando...</div>
            ) : docs.length === 0 ? (
              <div className="p-10 text-center text-xs text-gray-400">
                <div className="mx-auto w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 mb-3">
                  <Archive size={18} />
                </div>
                Nenhuma pendência de ingestão encontrada.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {docs.map((row) => {
                  const isActive = String(row?.id || '') === String(selectedDocId || '');
                  const title = resolveFileName(row);
                  const dt = toDateLabel(row?.createdAt);
                  const cat = String(row?.sourceKind === 'portal_upload' ? 'PORTAL_UPLOAD' : row?.classification?.category || row?.category || '').trim() || 'TRIAGEM';
                  const isPortalRow = String(row?.sourceKind || '') === 'portal_upload';

                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => handlePickDoc(row)}
                      className={
                        'w-full text-left px-4 py-3 hover:bg-gray-50 ' +
                        (isActive ? 'bg-blue-50/60 border-l-4 border-blue-400' : 'border-l-4 border-transparent')
                      }
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                          {isPortalRow ? <Inbox size={16} /> : <FileText size={16} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-extrabold text-slate-800 truncate" title={title}>
                            {title}
                          </div>
                          {isPortalRow ? (
                            <div className="mt-1 text-[11px] font-bold text-slate-500 truncate" title={resolveClientName(row)}>
                              {resolveClientName(row)}
                            </div>
                          ) : null}
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-[10px] font-bold text-slate-500 truncate">{dt}</span>
                            <span className="text-[10px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {cat}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        <section data-testid="ingestion-detail" className="lg:col-span-8 min-h-0 flex flex-col">
          {!selectedDoc ? (
            <div className="flex-1 min-h-0 p-10 text-center text-gray-400 text-sm">
              Selecione um documento para iniciar a triagem.
            </div>
          ) : (
            <div className="flex-1 min-h-0 p-6 overflow-y-auto custom-scrollbar">
              <div className="bg-white border border-gray-300 rounded-2xl shadow-sm overflow-hidden">
                <div className="p-5 border-b border-gray-300 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-slate-800 truncate" title={safeTitle}>
                      {safeTitle}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 font-bold">
                      {toDateLabel(selectedDoc?.createdAt)}
                      <span className="mx-2 text-slate-300">|</span>
                      {safeCategory}
                    </div>
                  </div>

                  {safeFileUrl ? (
                    <a
                      href={safeFileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-bold hover:bg-slate-50"
                      title="Abrir em nova aba"
                    >
                      <ExternalLink size={14} />
                      Abrir
                    </a>
                  ) : null}
                </div>

                <div className="p-5">
                  {isPortalUpload ? (
                    <div className="mb-5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-blue-700">Upload do Portal</div>
                        <div className="mt-1 text-sm font-bold text-slate-700">Revise, aprove e arquive no GED ou devolva ao cliente.</div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprovePortal(selectedDoc)}
                          disabled={String(workingId || '') === String(selectedDoc?.id || '')}
                          className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-[10px] font-extrabold uppercase tracking-widest"
                        >
                          {String(workingId || '') === String(selectedDoc?.id || '') && workingAction === 'approve' ? 'Aprovando...' : 'Aprovar e Arquivar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRejectPortal(selectedDoc)}
                          disabled={String(workingId || '') === String(selectedDoc?.id || '')}
                          className="rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-[10px] font-extrabold uppercase tracking-widest"
                        >
                          {String(workingId || '') === String(selectedDoc?.id || '') && workingAction === 'reject' ? 'Rejeitando...' : 'Rejeitar/Devolver'}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {isWhatsAppTriage ? (
                    <div className="mb-5 rounded-2xl border border-[#C5A059]/35 bg-[#FFFDF8] shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-[#E8D8A7] flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-xs font-extrabold uppercase tracking-widest text-[#7A5C1B]">Perícia de WhatsApp</div>
                          <div className="mt-1 text-[11px] font-bold text-slate-600">Dossiê forense importado para triagem jurídica</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                          <span className="px-2 py-1 rounded bg-white border border-slate-200">Mensagens: {whatsAppMetrics.messagePreview.length}</span>
                          <span className="px-2 py-1 rounded bg-white border border-slate-200">Mídias: {whatsAppMetrics.mediaCount}</span>
                          <span className="px-2 py-1 rounded bg-white border border-slate-200">Órfãos: {whatsAppMetrics.orphansCount}</span>
                        </div>
                      </div>

                      <div className="p-4 space-y-4">
                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Resumo Executivo</div>
                          <div className="mt-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap">
                            {whatsAppSummary || 'Resumo executivo indisponível para esta triagem.'}
                          </div>
                        </div>

                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Relatório Técnico Local</div>
                          <div className="mt-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-mono text-slate-600 break-all">
                            {whatsAppMetrics.technicalReportPath || 'Caminho do relatório técnico não informado.'}
                          </div>
                        </div>

                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">Prévia das Mensagens Importadas</div>
                          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                            <div className="max-h-[320px] overflow-y-auto custom-scrollbar divide-y divide-slate-100">
                              {whatsAppMetrics.messagePreview.length ? whatsAppMetrics.messagePreview.slice(0, 40).map((row, index) => (
                                <div key={`${row?.index || index}-${row?.timestamp || ''}`} className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                                    <span>{row?.timestamp || 'Sem data'}</span>
                                    <span>•</span>
                                    <span>{row?.author || 'Sem autor'}</span>
                                    {row?.linked_file ? <><span>•</span><span>{row.linked_file}</span></> : null}
                                  </div>
                                  <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{String(row?.text || '—').trim() || '—'}</div>
                                </div>
                              )) : (
                                <div className="px-4 py-6 text-sm text-slate-400">Nenhuma mensagem disponível nesta triagem.</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {previewLoading ? (
                    <div className="rounded-xl border border-slate-200 p-10 text-center text-xs text-slate-500 bg-slate-50">
                      Resolvendo pré-visualização...
                    </div>
                  ) : safeFileUrl ? (
                    <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                      <iframe
                        title="Pré-visualização"
                        src={safeFileUrl}
                        className="w-full h-[420px]"
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-xs text-slate-400">
                      {previewError || 'Pré-visualização indisponível para este item.'}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                      Análise documental assistida
                    </div>
                    <button
                      type="button"
                      onClick={handleAnalyzeDocument}
                      disabled={!safeFileUrl || aiLoading}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#C5A059]/40 bg-white text-[#7A5C1B] text-xs font-extrabold uppercase tracking-widest shadow-sm hover:bg-[#FFF8EC] disabled:opacity-50"
                    >
                      {aiLoading ? <i className="fas fa-spinner fa-spin" /> : <Sparkles size={14} />}
                      {aiLoading ? 'Extraindo...' : '✨ Extrair Dados com IA'}
                    </button>
                  </div>

                  {aiError ? (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-bold text-amber-900">
                      {aiError}
                    </div>
                  ) : null}

                  {aiResult?.success && aiResult?.extractedData ? (
                    <div className="mt-4 rounded-2xl border bg-[#FFFDF8] shadow-sm" style={{ borderColor: '#C5A059' }}>
                      <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(197,160,89,0.35)' }}>
                        <div className="text-xs font-extrabold uppercase tracking-widest text-[#7A5C1B]">
                          Resultado da Análise
                        </div>
                        <div className="mt-1 text-[11px] font-bold text-slate-600">
                          {String(aiResult?.documentType || 'Documento').trim()} · Qualidade {String(aiResult?.quality || 'GOOD').trim()}
                        </div>
                      </div>

                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-[#E8D8A7] bg-white px-3 py-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Nome</div>
                          <div className="mt-1 text-sm font-bold text-slate-800">{toTitleCasePt(aiResult.extractedData.name) || '—'}</div>
                        </div>
                        <div className="rounded-xl border border-[#E8D8A7] bg-white px-3 py-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">CPF</div>
                          <div className="mt-1 text-sm font-bold text-slate-800">{String(aiResult.extractedData.cpf || '—').trim() || '—'}</div>
                        </div>
                        <div className="rounded-xl border border-[#E8D8A7] bg-white px-3 py-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Data de Nascimento</div>
                          <div className="mt-1 text-sm font-bold text-slate-800">{String(aiResult.extractedData.birthDate || '—').trim() || '—'}</div>
                        </div>
                        <div className="rounded-xl border border-[#E8D8A7] bg-white px-3 py-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Nome da Mãe</div>
                          <div className="mt-1 text-sm font-bold text-slate-800">{String(aiResult.extractedData.motherName || '—').trim() || '—'}</div>
                        </div>
                        <div className="rounded-xl border border-[#E8D8A7] bg-white px-3 py-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Nome do Pai</div>
                          <div className="mt-1 text-sm font-bold text-slate-800">{String(aiResult.extractedData.fatherName || '—').trim() || '—'}</div>
                        </div>
                        <div className="rounded-xl border border-[#E8D8A7] bg-white px-3 py-2 md:col-span-2">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Filiação</div>
                          <div className="mt-1 text-sm font-bold text-slate-800">{String(aiResult.extractedData.filiation || '—').trim() || '—'}</div>
                        </div>
                      </div>

                      <div className="px-4 pb-4">
                        <button
                          type="button"
                          onClick={handleCreateClientFromAI}
                          className="inline-flex w-full items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#20243B] text-white text-xs font-extrabold uppercase tracking-widest hover:opacity-95"
                        >
                          <UserRoundPlus size={14} />
                          👤 Criar Novo Cliente
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-6">
                    <>
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">
                        Vincular a Cliente ou Processo
                      </div>
                      <SmartEntityLinker
                        onSelect={(entity) => {
                          if (!entity?.id) return;
                          setSelectedEntity({
                            id: entity.id,
                            type: entity.type,
                            name: entity.name,
                            conflict: entity.conflict || null,
                            conflictSummary: entity.conflictSummary || null,
                          });
                          setNotice('');
                          setError('');
                        }}
                      />

                      {selectedEntity ? (
                        <div className={`mt-3 rounded-xl p-3 flex items-start justify-between gap-3 ${selectedEntity?.conflict ? 'border border-red-200 bg-red-50' : 'border border-emerald-200 bg-emerald-50'}`}>
                          <div className="text-xs text-emerald-900 font-bold min-w-0">
                            <div className="flex items-center gap-2">
                              <Link2 size={14} />
                              <span className="truncate" title={toTitleCasePt(selectedEntity.name)}>
                                {toTitleCasePt(selectedEntity.name)}
                              </span>
                            </div>
                            <div className="mt-1 text-[10px] text-emerald-700 font-extrabold uppercase tracking-widest">
                              {selectedEntity.type === 'client' ? 'CLIENTE' : 'PROCESSO'}
                            </div>
                            {selectedEntity?.conflict ? (
                              <div className="mt-2 rounded-lg border border-red-200 bg-white/80 px-3 py-2 text-[11px] text-red-800 font-bold">
                                <div className="uppercase tracking-widest text-[10px] font-extrabold">⚠️ Alerta de Conflito Ético</div>
                                <div className="mt-1">{selectedEntity?.conflict?.message || selectedEntity?.conflictSummary?.message}</div>
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedEntity(null)}
                            className={`text-[10px] font-extrabold uppercase tracking-widest ${selectedEntity?.conflict ? 'text-red-800 hover:text-red-600' : 'text-emerald-800 hover:text-red-600'}`}
                          >
                            Limpar
                          </button>
                        </div>
                      ) : null}

                      {!isPortalUpload ? (
                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            onClick={handleLink}
                            disabled={!selectedEntity || linking}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#20243B] text-white text-xs font-extrabold uppercase tracking-widest shadow-sm hover:opacity-95 disabled:opacity-50"
                          >
                            {linking ? <i className="fas fa-spinner fa-spin" /> : <Archive size={14} />}
                            {linking ? 'Vinculando...' : 'Vincular e Arquivar'}
                          </button>
                        </div>
                      ) : null}
                    </>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
      )}
    </div>
  );
};

export default IngestionHub;
